import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { Switch } from '@/components/ui/switch';
import { format, parseISO } from 'date-fns';
import { CORE, NEUTRAL } from '@/utils/dls-tokens';
import type { UptimeDataPoint, Period } from '@/types/metrics';

interface UptimeTrendChartProps {
  data: UptimeDataPoint[];
  period: Period;
  averageUptime: number;
  /** Start of full-fleet polling; buckets before this are reconstructed from partial observations */
  dataQualityEpoch?: string | null;
}

export function UptimeTrendChart({
  data,
  period,
  averageUptime,
  dataQualityEpoch,
}: UptimeTrendChartProps) {
  const [showFullRange, setShowFullRange] = useState(false);

  const formatXAxis = (time: string) => {
    const date = parseISO(time);
    if (period === '24h') {
      return format(date, 'HH:mm');
    }
    return format(date, 'MMM d');
  };

  // Compute y-axis domain: always cap at 100, relative min is 1% below lowest reading.
  // Null buckets are data gaps — excluded from the min
  const yDomain: [number, number] = (() => {
    if (showFullRange) return [0, 100];
    const values = data
      .map((d) => d.uptimePercent)
      .filter((v): v is number => v !== null);
    if (values.length === 0) return [0, 100];
    const minValue = Math.min(...values);
    const relativeMin = Math.max(0, Math.floor(minValue - 1));
    return [relativeMin, 100];
  })();

  // Shade the reconstructed (pre-full-polling) region when it's in view.
  // ReferenceArea x-coordinates must be actual category values, so use the
  // last data point at or before the epoch as the region boundary
  const partialRegion: { x1: string; x2: string } | null = (() => {
    if (!dataQualityEpoch || data.length === 0) return null;
    const epochMs = parseISO(dataQualityEpoch).getTime();
    if (parseISO(data[0].time).getTime() >= epochMs) return null;
    const boundary = [...data].reverse().find((d) => parseISO(d.time).getTime() <= epochMs);
    if (!boundary || boundary.time === data[0].time) return null;
    return { x1: data[0].time, x2: boundary.time };
  })();

  return (
    <Card>
      <CardHeader className="dls-flex dls-flex-row dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
            Uptime Trend
            <InfoTooltip text="Historical fleet uptime over time. Each data point represents the percentage of Nodes online during that interval." />
          </CardTitle>
          <p className="dls-text-sm dls-text-muted-foreground">Average: {averageUptime.toFixed(1)}%</p>
        </div>

        <label className="dls-flex dls-items-center dls-gap-2 dls-text-sm dls-text-muted-foreground dls-cursor-pointer dls-select-none">
          <Switch checked={showFullRange} onCheckedChange={setShowFullRange} />
          Show full range
        </label>
      </CardHeader>
      <CardContent>
        <div className="dls-h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="uptimeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CORE.neeveGreen} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={CORE.neeveGreen} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={NEUTRAL.quarternary} />
              <XAxis dataKey="time" tickFormatter={formatXAxis} stroke={NEUTRAL.secondary} fontSize={12} />
              <YAxis
                domain={yDomain}
                tickFormatter={(v) => `${v}%`}
                stroke={NEUTRAL.secondary}
                fontSize={12}
              />
              <Tooltip
                formatter={(value: number | string) =>
                  typeof value === 'number'
                    ? [`${value.toFixed(1)}%`, 'Uptime']
                    : ['No data', 'Uptime']
                }
                labelFormatter={(label) => format(parseISO(label as string), 'PPp')}
              />
              {partialRegion && (
                <ReferenceArea
                  x1={partialRegion.x1}
                  x2={partialRegion.x2}
                  fill={NEUTRAL.quarternary}
                  fillOpacity={0.35}
                  strokeOpacity={0}
                  label={{
                    value: 'Partial data (reconstructed)',
                    position: 'insideTopLeft',
                    fontSize: 11,
                    fill: NEUTRAL.secondary,
                  }}
                />
              )}
              <Area
                type="monotone"
                dataKey="uptimePercent"
                stroke={CORE.neeveGreen}
                strokeWidth={2}
                fill="url(#uptimeGradient)"
                connectNulls={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
